<template>
  <!-- 外层 .app-container 已含 padding:20px -->
  <div class="agent-wrap">
    <!-- 当前客服状态栏 -->
    <div class="agent-statusbar" v-if="agentId">
      <div class="agent-statusbar-left">
        <span class="agent-statusbar-name">{{ agent.nickname || agent.username || '客服' }}</span>
        <el-tag :type="onlineStatus === 1 ? 'success' : onlineStatus === 2 ? 'warning' : 'info'" size="small">
          {{ onlineStatus === 1 ? '在线' : onlineStatus === 2 ? '繁忙' : '离线' }}
        </el-tag>
      </div>
      <el-radio-group :value="onlineStatus" size="small" @input="changeStatus">
        <el-radio-button :label="1">在线</el-radio-button>
        <el-radio-button :label="2">繁忙</el-radio-button>
        <el-radio-button :label="0">离线</el-radio-button>
      </el-radio-group>
    </div>

    <div style="height: calc(100vh - 120px)" class="agent-dashboard">
    <!-- 轉接請求彈窗 -->
    <el-dialog
      :visible.sync="transferDialogVisible"
      title="轉接請求"
      width="420px"
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
    >
      <div v-if="incoming" class="transfer-body">
        <div class="transfer-text">
          客服 <b>{{ incoming.fromAgentNickname }}</b> 想把客戶
          <b>{{ incoming.customerName || ('會話 #' + incoming.sessionId) }}</b>
          轉接給你，是否接受？
        </div>
        <el-progress :percentage="progressPct" :show-text="false" :stroke-width="8"></el-progress>
        <div class="transfer-countdown">{{ transferCountdown }} 秒後自動拒絕</div>
      </div>
      <div slot="footer">
        <el-button @click="handleRejectTransfer">拒絕</el-button>
        <el-button type="primary" @click="handleAcceptTransfer">接受</el-button>
      </div>
    </el-dialog>

    <!-- 左側：會話列表 -->
    <div class="dash-sidebar">
      <div class="dash-tabs">
        <button
          class="dash-tab"
          :class="{ active: activeMenu === 'sessions' }"
          :style="tabStyle(activeMenu === 'sessions')"
          @click="handleMenuChange('sessions')"
        >
          <i class="el-icon-chat-dot-round"></i>
          我的會話
          <span class="dash-tab-badge" :style="badgeStyle(activeMenu === 'sessions', '#4f46e5')">
            {{ sessions.length }}
          </span>
        </button>
        <button
          class="dash-tab"
          :class="{ active: activeMenu === 'queue' }"
          :style="tabStyle(activeMenu === 'queue')"
          @click="handleMenuChange('queue')"
        >
          <i class="el-icon-user"></i>
          排隊
          <span class="dash-tab-badge" :style="badgeStyle(activeMenu === 'queue', '#ff4d4f')">
            {{ queueSessions.length }}
          </span>
        </button>
        <button
          v-if="canMonitor"
          class="dash-tab"
          :class="{ active: activeMenu === 'monitor' }"
          :style="tabStyle(activeMenu === 'monitor')"
          @click="handleMenuChange('monitor')"
        >
          <i class="el-icon-view"></i>
          全部會話
          <span class="dash-tab-badge" :style="badgeStyle(activeMenu === 'monitor', '#08979c')">
            {{ monitorSessions.length }}
          </span>
        </button>
      </div>

      <SessionListItem
        :sessions="displaySessions"
        :selected-id="activeMenu === 'monitor' ? monitorSelectedId : currentSessionId"
        :is-queue="activeMenu === 'queue'"
        :agent-id="agentId"
        :on-pick="queuePickHandler"
        @select="handleListSelect"
      />
    </div>

    <!-- 右側：聊天或空狀態 -->
    <div class="dash-main">
      <ChatPanel
        v-if="monitorSelectedId && monitorMeta"
        :session-id="monitorSelectedId"
        :agent-id="agentId"
        :agent-name="agent.nickname"
        readonly
        :session-meta="monitorMeta"
        :messages="messages[monitorSelectedId] || []"
        :typing-preview="typingPreviews[monitorSelectedId] || ''"
        :customer-read-watermark="customerReadWatermarks[monitorSelectedId] || 0"
        :current-session="monitorCurrentSession"
        :on-close="onCloseMonitor"
        @history-loaded="handleHistoryLoaded"
        @append-message="appendMessage"
      />
      <ChatPanel
        v-else-if="currentSessionId"
        :session-id="currentSessionId"
        :agent-id="agentId"
        :agent-name="agent.nickname"
        :messages="messages[currentSessionId] || []"
        :typing-preview="typingPreviews[currentSessionId] || ''"
        :customer-read-watermark="customerReadWatermarks[currentSessionId] || 0"
        :current-session="currentSessionObj"
        :on-close="onCloseChat"
        @history-loaded="handleHistoryLoaded"
        @append-message="appendMessage"
      />
      <div v-else class="dash-empty">
        <div class="dash-empty-icon">
          <i class="el-icon-chat-line-round"></i>
        </div>
        <div class="dash-empty-title">
          {{ activeMenu === 'monitor' ? '選擇左側會話旁聽' : '選擇左側會話開始服務' }}
        </div>
        <div class="dash-empty-sub">
          {{ emptySubText }}
        </div>
        <el-button
          v-if="queueSessions.length > 0 && activeMenu !== 'monitor'"
          type="primary"
          size="large"
          icon="el-icon-download"
          class="dash-empty-btn"
          @click="handlePick"
        >
          接入排隊客戶
        </el-button>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import SessionListItem from './components/SessionListItem.vue'
import ChatPanel from './components/ChatPanel.vue'
import {
  getMySessions,
  getQueue,
  pickSession,
  getAllActiveSessions,
  acceptTransfer,
  rejectTransfer
} from '@/api/customerService/agent'
import { createStompClient } from '../components/useWs'
import base from '@/api/base'

const SESSION_LIST_POLL_MS = 5000

export default {
  name: 'CustomerSessionList',
  components: { SessionListItem, ChatPanel },
  data() {
    return {
      activeMenu: 'sessions',
      sessions: [],
      queueSessions: [],
      queueSize: 0,
      currentSessionId: null,
      monitorSessions: [],
      monitorSelectedId: null,
      monitorMeta: null,
      // 会话级实时状态：sessionId -> 数据
      messages: {},
      typingPreviews: {},
      customerReadWatermarks: {},
      // 转接请求
      incoming: null,
      transferCountdown: 30,
      // timers
      sessionsTimer: null,
      monitorTimer: null,
      transferTimer: null
    }
  },
  computed: {
    agent() {
      return this.$store.state.customerService.agentInfo || {}
    },
    agentId() {
      return this.$store.state.customerService.agentId
    },
    onlineStatus() {
      const v = this.$store.state.customerService.onlineStatus
      return v == null ? null : Number(v)
    },
    canMonitor() {
      const perms = this.agent.permissions || []
      return perms.indexOf('cs:chat:monitor') !== -1
    },
    displaySessions() {
      if (this.activeMenu === 'queue') return this.queueSessions
      if (this.activeMenu === 'monitor') return this.monitorRowsAsSessions
      return this.sessions
    },
    monitorRowsAsSessions() {
      return this.sortedMonitorSessions.map(this.monitorItemToChatSession)
    },
    sortedMonitorSessions() {
      const arr = this.monitorSessions.slice().sort((a, b) => {
        const an = a.agentNickname || ''
        const bn = b.agentNickname || ''
        if (an !== bn) {
          return an.localeCompare(bn, 'zh-Hant-CN')
        }
        const t1 = this.stringTime(a.lastMessageTime)
        const t2 = this.stringTime(b.lastMessageTime)
        return t2 - t1
      })
      return arr
    },
    queuePickHandler() {
      return this.activeMenu === 'queue' ? this.handlePick : null
    },
    currentSessionObj() {
      if (!this.currentSessionId) return null
      return this.sessions.find(s => String(s.id) === String(this.currentSessionId)) || null
    },
    monitorCurrentSession() {
      return null
    },
    transferDialogVisible: {
      get() {
        return !!this.incoming
      },
      set(v) {
        if (!v) this.handleRejectTransfer()
      }
    },
    progressPct() {
      const total = this.ttlTotal
      return total > 0 ? Math.round((this.transferCountdown / total) * 100) : 0
    },
    ttlTotal() {
      return this.incoming && this.incoming.ttlSeconds ? this.incoming.ttlSeconds : 30
    },
    emptySubText() {
      let t = this.sessions.length > 0 ? ('目前有 ' + this.sessions.length + ' 個進行中的對話') : '目前沒有進行中的對話'
      if (this.queueSessions.length > 0) t += ' · ' + this.queueSessions.length + ' 個客戶排隊中'
      if (this.canMonitor && this.activeMenu === 'monitor') t += ' · 全部 ' + this.monitorSessions.length + ' 個人工服務中會話'
      return t
    }
  },
  watch: {
    agentId(v) {
      if (v) { this.autoGoOnline(); this.refreshSessions(); this.subscribeAgentChannel(v) }
    },
    activeMenu() {
      this.handleMenuWatch()
    },
    incoming(val) {
      if (this.transferTimer) clearInterval(this.transferTimer)
      this.transferTimer = null
      if (!val) return
      const ttl = val.ttlSeconds || 30
      let remain = ttl
      this.transferCountdown = ttl
      this.transferTimer = setInterval(() => {
        remain -= 1
        this.transferCountdown = Math.max(0, remain)
        if (remain <= 0) {
          if (this.transferTimer) clearInterval(this.transferTimer)
          this.transferTimer = null
          rejectTransfer(val.transferId)
            .catch(() => { /* ignore */ })
            .then(() => {
              this.incoming = null
            })
        }
      }, 1000)
    }
  },
  created() {
    this._listening = new Set()
    this._ws = null
    this.initWs()
    this.handleMenuWatch()
    this.refreshSessions()
    this.autoGoOnline()
    this.sessionsTimer = setInterval(this.refreshSessions, SESSION_LIST_POLL_MS)
  },
  beforeDestroy() {
    if (this.sessionsTimer) clearInterval(this.sessionsTimer)
    if (this.monitorTimer) clearInterval(this.monitorTimer)
    if (this.transferTimer) clearInterval(this.transferTimer)
    if (this._ws && this._ws.disconnect) this._ws.disconnect()
  },
  methods: {
    // ===== 客服在线状态 =====
    // 进入工作台自动上线（若当前已是 在线/繁忙 则不重复置在线）
    autoGoOnline() {
      if (!this.agentId) return
      if (this.onlineStatus === 1 || this.onlineStatus === 2) return
      this.$store.dispatch('customerService/setOnline', 1)
    },
    // 手动切换 在线/繁忙/离线（0=离线 1=在线 2=繁忙）
    changeStatus(status) {
      if (!this.agentId) return
      this.$store.dispatch('customerService/setOnline', status)
    },
    // ===== WebSocket =====
    wsBrokerUrl() {
      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
      let srvHost = base.url ? base.url.replace(/^https?:\/\//, '') : (window.location.host + '/service')
      const wsUrlRaw = proto + '://' + srvHost + '/service/api/v1/ws/chat'
      return wsUrlRaw + '?isAjax=1'
    },
    initWs() {
      this._ws = createStompClient(this.wsBrokerUrl())
      this._ws.onConnect = () => { /* 轻量客户端的 subscribed 列表会自动在重连后重订阅 */ }
      this._ws.connect()
    },
    // Server pushes customer messages to /topic/agent/{agentId}; workbench must subscribe it.
    subscribeAgentChannel(agentId) {
      if (!this._ws || !agentId || this._agentSubscribed) return
      this._agentSubscribed = true
      this._ws.subscribe('/topic/agent/' + agentId, (frame) => this.handleSessionFrame(frame))
    },
    subscribeSession(sid) {
      if (!this._ws) return
      if (this._listening.has(sid)) return
      this._listening.add(sid)
      this._ws.subscribe('/topic/session/' + sid, frame => this.handleSessionFrame(frame))
    },
    handleSessionFrame(frame) {
      try {
        let data = frame.data
        if (data == null) data = {}
        if (typeof data === 'string') { try { data = JSON.parse(frame.body || data) } catch (e) { data = {} } }
        let sid = data.sessionId
        if (sid == null && frame.headers && frame.headers.destination) {
          const m = String(frame.headers.destination).match(/\/(\d+)\s*$/)
          if (m) {
            try { data = JSON.parse(frame.body) } catch (e) { data = {} }
            if (!data.sessionId) data.sessionId = Number(m[1])
            sid = data.sessionId
          }
        }
        if (sid == null) return
        if (data.wsType === 'TYPING_PREVIEW') {
          this.$set(this.typingPreviews, sid, data.content || '')
          return
        }
        if (data.wsType === 'READ_RECEIPT') {
          if (data.reader === 'CUSTOMER') {
            const cur = this.customerReadWatermarks[sid] || 0
            if (Number(data.lastReadMsgId) > Number(cur)) {
              this.$set(this.customerReadWatermarks, sid, data.lastReadMsgId)
            }
          }
          return
        }
        // 普通消息
        this.appendMessage(data)
      } catch (e) { /* ignore */ }
    },
    appendMessage(msg) {
      const sid = msg.sessionId
      if (!sid) return
      const existing = this.messages[sid] || []
      if (existing.some(m => m.id === msg.id)) return
      this.$set(this.messages, sid, existing.concat(msg))
      if (msg.senderType === 0 && this.typingPreviews[sid]) {
        this.$set(this.typingPreviews, sid, '')
      }
    },
    handleHistoryLoaded(sid, msgs) {
      this.$set(this.messages, sid, msgs)
    },

    // ===== 数据刷新 =====
    ensureAgent() {
      if (this.agentId) return
      const localId = localStorage.getItem('cs_agentId')
      if (localId) {
        this.$store.commit('customerService/SET_AGENT', { id: localId })
        return
      }
      const label = window.SITE_CONFIG['routerLabel'] || (this.$route && this.$route.query.label)
      try {
        const ui = JSON.parse(localStorage.getItem(label + '_sys_userInfo') || '{}')
        if (ui.userName) this.$store.dispatch('customerService/fetchAgentInfo', ui.userName)
      } catch (e) { /* ignore */ }
    },
    async refreshSessions() {
      if (!this.agentId) { this.ensureAgent(); return }
      try {
        const results = await Promise.all([getMySessions(this.agentId), getQueue()])
        const mySessions = (results[0].result) || []
        const queueData = results[1].result || {}
        this.sessions = mySessions
        this.queueSessions = queueData.sessions || []
        this.queueSize = queueData.queueSize || this.queueSessions.length
        // 更新客户已读水位线
        const that = this
        mySessions.forEach(s => {
          const wm = that.customerReadWatermarks[s.id] || 0
          if (s.customerLastReadMsgId && Number(s.customerLastReadMsgId) > Number(wm)) {
            that.$set(that.customerReadWatermarks, s.id, s.customerLastReadMsgId)
          }
        })
        this.subscribeAgentChannel(this.agentId)
        mySessions.forEach(s => this.subscribeSession(s.id))
      } catch (e) { /* ignore */ }
    },
    async refreshMonitor() {
      if (!this.canMonitor) return
      try {
        const res = await getAllActiveSessions()
        this.monitorSessions = res.result || []
        this.monitorSessions.forEach(s => this.subscribeSession(s.id))
      } catch (e) {
        this.$message.error(e.message || '載入全部會話失敗')
      }
    },
    handleMenuWatch() {
      if (this.monitorTimer) clearInterval(this.monitorTimer)
      this.monitorTimer = null
      if (this.activeMenu === 'monitor') {
        if (this.canMonitor) {
          this.refreshMonitor()
          this.monitorTimer = setInterval(this.refreshMonitor, SESSION_LIST_POLL_MS)
        }
      } else {
        this.monitorSelectedId = null
        this.monitorMeta = null
      }
    },

    // ===== 交互 =====
    handleMenuChange(key) {
      this.activeMenu = key
      if (key === 'monitor') {
        this.currentSessionId = null
      } else {
        this.monitorSelectedId = null
        this.monitorMeta = null
      }
    },
    handleListSelect(id) {
      if (this.activeMenu === 'monitor') {
        this.handleMonitorSelect(id)
      } else {
        this.handleNormalSelect(id)
      }
    },
    handleNormalSelect(id) {
      this.monitorSelectedId = null
      this.monitorMeta = null
      this.currentSessionId = id
      this.subscribeSession(id)
    },
    handleMonitorSelect(id) {
      const row = this.monitorSessions.find(s => s.id === id)
      this.currentSessionId = null
      this.monitorSelectedId = id
      this.monitorMeta = row
        ? {
            customerName: row.customerName,
            channel: row.channel,
            subtitle: '負責客服：' + (row.agentNickname || row.agentId) + ' · ' + row.channel + ' · 會話 #' + row.id
          }
        : null
      if (row) this.subscribeSession(row.id)
    },
    onCloseChat() {
      this.currentSessionId = null
    },
    onCloseMonitor() {
      this.monitorSelectedId = null
      this.monitorMeta = null
    },
    async handlePick() {
      try {
        await pickSession(this.agentId)
        await this.refreshSessions()
        this.$message.success('已接入客戶')
      } catch (e) {
        this.$message.error(e.message || '接入失敗')
      }
    },
    async handleAcceptTransfer() {
      if (!this.incoming) return
      try {
        await acceptTransfer(this.incoming.transferId)
        this.incoming = null
        this.$message.success('已接受轉接')
        await this.refreshSessions()
      } catch (e) {
        this.$message.error(e.message || '接受失敗')
      }
    },
    async handleRejectTransfer() {
      if (!this.incoming) return
      try {
        await rejectTransfer(this.incoming.transferId)
      } catch (e) { /* ignore */ } finally {
        this.incoming = null
      }
    },

    // ===== 工具 =====
    monitorItemToChatSession(m) {
      return {
        id: m.id,
        customerId: 0,
        customerName: m.customerName,
        channel: m.channel,
        customerType: '',
        status: m.status,
        agentId: m.agentId,
        agentNickname: m.agentNickname,
        lastMessageTime: m.lastMessageTime,
        createTime: m.createTime,
        unreadCount: 0
      }
    },
    stringTime(ts) {
      if (!ts) return 0
      const d = new Date(ts)
      return isNaN(d.getTime()) ? 0 : d.getTime()
    },
    tabStyle(active) {
      return active
        ? { background: '#4f46e5', color: '#fff', borderRadius: '8px 8px 0 0' }
        : { background: 'transparent', color: '#909399', borderRadius: '0' }
    },
    badgeStyle(active, color) {
      return active
        ? { background: '#fff', color: color }
        : { background: color, color: '#fff' }
    }
  }
}
</script>

<style scoped>
.agent-wrap {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.agent-statusbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 8px 14px;
}
.agent-statusbar-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.agent-statusbar-name {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}
.agent-dashboard {
  display: flex;
  background: #f5f7fa;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  overflow: hidden;
}
.transfer-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.transfer-countdown {
  font-size: 12px;
  color: #909399;
}
.dash-sidebar {
  width: 320px;
  background: #fff;
  border-right: 1px solid #ebeef5;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}
.dash-tabs {
  display: flex;
  border-bottom: 1px solid #ebeef5;
  background: #f5f7fa;
  flex-wrap: wrap;
}
.dash-tab {
  flex: 1;
  min-width: 90px;
  padding: 14px 0;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}
.dash-tab-badge {
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  font-size: 11px;
  line-height: 18px;
  display: inline-block;
}
.dash-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #fff;
  min-width: 0;
}
.dash-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  flex-direction: column;
  gap: 16px;
}
.dash-empty-icon {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e0e7ff, #c7d2fe);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4f46e5;
  font-size: 48px;
}
.dash-empty-title {
  font-size: 18px;
  font-weight: 600;
  color: #909399;
}
.dash-empty-sub {
  color: #909399;
  font-size: 14px;
}
.dash-empty-btn {
  margin-top: 8px;
}
</style>